import { collectStation, delStation } from '@/api/powerStation'
import useMessage from '@/hooks/useMessage'
import useStationListStore from '@/stores/stationList'
import { Station, StationVirtualPoint } from '@/type/stationType'
import { COLLECT_TYPE, SITE_STATUS, siteStatusLists, STATION_AUTH } from '@/utils/const'
import { DeleteOutlined, EditOutlined, ExclamationCircleFilled, ShareAltOutlined } from '@ant-design/icons'
import { Flex, Modal, Table } from 'antd'
import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import ShareModal from './ShareModal'
import appConfig from '@/config'
import { getLabelData } from '@/utils/getData'
import useGlobalConfigStore from '@/stores/globalConfig'
import { useNavigate } from 'react-router-dom'

const SiteTable: React.FC<{ station: Station[] }> = ({ station }) => {
  const { t } = useTranslation()
  const setUpdateTime = useGlobalConfigStore(state => state.setUpdateTime)
  const setInitFinished = useGlobalConfigStore(state => state.setInitFinished)
  const { message } = useMessage()
  const navigate = useNavigate()
  const stationInit = useStationListStore(state => state.stationInit)
  const access_device = useStationListStore(state => state.access_device)
  const gateway = useStationListStore(state => state.gateway)
  const mqtt_serve = useStationListStore(state => state.mqtt_serve)
  type StationTableItem = {
    id: number
    site: { id: number; name: string; address: string; is_collect: number; image: string | undefined }
    run_status: SITE_STATUS
    siteType: string
    owner: { name: string; time: string }
    installedCapacity: string
    bessCapacity: string
    realtimePower: string
    yieldToday: string
    action: { label: string; icon: JSX.Element }[]
  }

  const abortController = useRef<any>(null)
  const [tableStation, setTableStation] = useState<StationTableItem[]>([])
  const [startRealTime, setStartRealTime] = useState(false)

  useEffect(() => {
    const tableStationTemp: StationTableItem[] = station.map(item => {
      const action: { label: string; icon: JSX.Element }[] = []
      if (item.station_auth !== STATION_AUTH.VIEW) {
        action.push({
          label: 'edit',
          icon: <EditOutlined style={{ color: 'var(--main-warning)' }} className='tableBtn' />,
        })
      }
      if (item.station_auth === STATION_AUTH.MANAGE) {
        action.push({
          label: 'share',
          icon: (
            <ShareAltOutlined style={{ color: '#1877f2' }} className='tableBtn' onClick={() => handleShare(item.id)} />
          ),
        })
      }
      action.push({
        label: 'delete',
        icon: (
          <DeleteOutlined
            style={{ color: 'var(--main-error)' }}
            className='tableBtn'
            onClick={() => handleDelete(item.id)}
          />
        ),
      })
      return {
        id: item.id,
        site: {
          id: item.id,
          name: item.station_name,
          address: item.address,
          is_collect: item.is_collect,
          image: item.station_image[0],
        },
        run_status: item.run_status,
        siteType: item.station_type_name,
        owner: { name: item.create_name, time: item.create_time },
        installedCapacity: item.installed_capacity,
        bessCapacity: item.rated_capacity,
        realtimePower: '0.00',
        yieldToday: '0.00',
        action,
      }
    })
    setTableStation(tableStationTemp)
    setStartRealTime(!startRealTime)
  }, [station])

  useEffect(() => {
    abortController.current?.abort()
    abortController.current = new AbortController()
    getRealTimeData(abortController.current.signal)
  }, [startRealTime])

  const getRealTimeData = (abortSignal: AbortSignal) => {
    return new Promise((resolve, reject) => {
      const task = async () => {
        const tableStationTemp = [...tableStation]
        for (let oneStation of tableStationTemp) {
          const res = await getPandKRA(station.find(item => item.id === oneStation.id))
          oneStation.realtimePower = res.realtimePower.toFixed(2)
          oneStation.yieldToday = res.yieldToday.toFixed(2)
          if (abortSignal.aborted) {
            // console.log('Read Aborted', oneStation.site.name, res)
            reject('stop')
            break
          }
          setTableStation([...tableStationTemp])
          setUpdateTime()
        }
        resolve(true)
      }
      abortSignal.addEventListener(
        'abort',
        () => {
          reject('stop')
        },
        { once: true }
      )
      task()
    })
  }

  const getPandKRA = async (station: Station | undefined) => {
    if (station === undefined) {
      return { realtimePower: 0, yieldToday: 0 }
    }
    const label = station.label?.find(item => item.type === 'fd')
    if (label) {
      const realtimePowerPoint = label.points.find(point => point.property_code === 'P')
      const yieldTodayPoint = label.points.find(point => point.property_code === 'today-KRA')
      if (realtimePowerPoint && yieldTodayPoint) {
        const points: (StationVirtualPoint & { label_name: string })[] = [
          { ...realtimePowerPoint, label_name: 'realtimePower' },
          { ...yieldTodayPoint, label_name: 'yieldToday' },
        ]
        try {
          const res = await getLabelData(points, access_device, gateway, mqtt_serve)
          return { realtimePower: res[0].value, yieldToday: res[1].value }
        } catch (e) {
          console.log(e)
          return { realtimePower: 0, yieldToday: 0 }
        }
      } else {
        return { realtimePower: 0, yieldToday: 0 }
      }
    } else {
      return { realtimePower: 0, yieldToday: 0 }
    }
  }

  const [columns, setColumns] = useState<any>([])
  useEffect(() => {
    setColumns([
      {
        title: t('site.siteName'),
        dataIndex: 'site',
        fixed: 'left',
        width: '25%',
        render: (v: any) => {
          const { image, name, address, is_collect, id } = v
          const collectImg =
            is_collect === COLLECT_TYPE.COLLECT ? '/assets/web/collected.svg' : '/assets/web/collect.svg'
          return (
            <div
              className='table-name'
              onClick={() => {
                setInitFinished(false)
                navigate(`/site/${id}/main`)
              }}>
              <img src={appConfig.staticFile + image} style={{ width: 40, objectFit: 'contain' }} />
              <div className='table-name-text' style={{ marginLeft: 10 }}>
                <span className='collectImg-name'>
                  {name}
                  <img
                    className='collect'
                    src={collectImg}
                    onClick={e =>
                      handleCollect(
                        id,
                        is_collect === COLLECT_TYPE.COLLECT ? COLLECT_TYPE.NOT_COLLECT : COLLECT_TYPE.COLLECT,
                        e
                      )
                    }
                  />
                </span>
                <span>{address}</span>
              </div>
            </div>
          )
        },
      },
      {
        title: t('site.status'),
        dataIndex: 'run_status',
        width: '6%',
        render: (v: any) => {
          return (
            <div
              className={`tableStatus plantList-before plantList${
                siteStatusLists.find(item => item.value === v)?.label || 'unknown'
              }`}>
              {t('status.' + siteStatusLists.find(item => item.value === v)?.label || 'unknown')}
            </div>
          )
        },
      },
      {
        title: t('site.siteType'),
        dataIndex: 'siteType',
        width: '9%',
      },
      {
        title: t('site.owner'),
        dataIndex: 'owner',
        width: '10%',
        render: (v: any) => {
          const { name, time } = v
          return (
            <Flex gap={0} vertical className='tableOwner'>
              <span>{name}</span>
              <span>{time}</span>
            </Flex>
          )
        },
      },
      {
        title: t('site.installedCapacity'),
        dataIndex: 'installedCapacity',
        // render: (v: any) => {
        //   return <NumberScroller targetNumber={v} />
        // },
        width: '12%',
      },
      {
        title: t('site.bess'),
        dataIndex: 'bessCapacity',
        // render: (v: any) => {
        //   return <NumberScroller targetNumber={v} />
        // },
        width: '10%',
      },
      {
        title: t('site.realtimePower'),
        dataIndex: 'realtimePower',
        // render: (v: any) => {
        //   return <NumberScroller targetNumber={v} />
        // },
        width: '10%',
      },
      {
        title: t('site.yieldToday'),
        dataIndex: 'yieldToday',
        // render: (v: any) => {
        //   return <NumberScroller targetNumber={v} />
        // },
        width: '10%',
      },
      {
        title: t('common.action'),
        dataIndex: 'action',
        width: '8%',
        render: (v: { label: string; icon: JSX.Element }[]) => {
          return (
            <Flex gap={15}>
              {v.map((v1: any) => (
                <div key={v1.label}>{v1.icon}</div>
              ))}
            </Flex>
          )
        },
      },
    ])
  }, [t])

  const [shareOpen, setShareOpen] = useState(false)
  const [modalStationId, setModalStationId] = useState(0)

  const handleShare = (id: number) => {
    setModalStationId(id)
    setShareOpen(true)
  }

  const handleDelete = (id: number) => {
    Modal.confirm({
      title: t('common.deleteConfirm'),
      content: t('common.deleteTip'),
      icon: <ExclamationCircleFilled />,
      okText: t('common.confirm'),
      cancelText: t('common.cancel'),
      onOk: () => confirmDelete(id),
      centered: true,
    })
  }

  const confirmDelete = async (id: number) => {
    // console.log(id)
    try {
      const res = await delStation({ station_id: id })
    } catch (error: any) {
      console.log(error)
      message.error(error.message || error.msg || error)
      return
    }
    message.success(t('common.deleteSuccess'))
    try {
      await stationInit()
    } catch (error: any) {
      console.log(error)
      message.error(error.message || error.msg || error)
    }
  }

  const handleCollect = async (id: number, type: number | string, e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation()
    let res: any
    try {
      res = await collectStation({ station_id: id, type })
    } catch (error: any) {
      console.log(error)
      message.error(error.message || error.msg || error)
      return
    }
    if (res.code !== 1001) {
      message.error(res.msg)
    } else {
      message.success(type === COLLECT_TYPE.COLLECT ? t('site.collectSuccess') : t('site.cancelCollectSuccess'))
      try {
        await stationInit()
      } catch (error: any) {
        console.log(error)
        message.error(error.message || error.msg || error)
      }
    }
  }

  return (
    <>
      <Table
        scroll={{
          x: 'max-content',
          y: window.innerHeight - 365,
        }}
        dataSource={tableStation}
        columns={columns}
        rowKey={'id'}
      />
      <ShareModal open={shareOpen} stationId={modalStationId} onClose={() => setShareOpen(false)} />
    </>
  )
}

export default SiteTable
